<template>
  <div class="report-wrap">
    <div id="capture" class="report">
      <!-- ============================ 封面区 ============================ -->
      <header class="rp-cover">
        <div class="rp-brand">
          <span class="rp-brand-mark"></span>
          <span>数据贯通成熟度评价</span>
        </div>
        <h1 class="rp-title">数据贯通成熟度评价报告</h1>
        <p class="rp-subtitle">
          {{ editionName }} · {{ indicatorCount }} 项指标 · 六能力域加权定级
        </p>

        <dl class="rp-meta">
          <div class="rp-meta-item">
            <dt>评价对象</dt>
            <dd>{{ meta.subjectName || '--' }}</dd>
          </div>
          <div class="rp-meta-item">
            <dt>评价范围</dt>
            <dd>{{ scopeName }}</dd>
          </div>
          <div class="rp-meta-item">
            <dt>评价角色</dt>
            <dd>{{ roleName }}</dd>
          </div>
          <div class="rp-meta-item">
            <dt>报告日期</dt>
            <dd>{{ today }}</dd>
          </div>
        </dl>
      </header>

      <!-- ============================ 一、评价结论 ============================ -->
      <section class="rp-section">
        <h2 class="rp-h2"><span class="rp-h2-no">一</span>评价结论</h2>

        <div class="rp-conclusion">
          <div class="rp-level-box">
            <span class="rp-level-label">成熟度等级</span>
            <span class="rp-level-value">{{ finalLevel || '--' }}</span>
            <span class="rp-level-name">{{ levelName }}</span>
          </div>

          <div class="rp-score-box">
            <div class="rp-score-main">
              <span class="rp-score-num">{{ scoreText }}</span>
              <span class="rp-score-unit">/ 5.0</span>
            </div>
            <div class="rp-score-sub">百分制 {{ percentText }}</div>
            <div class="rp-score-sub">
              原始等级 {{ rawLevel || '--' }}
              <template v-if="selfDeviation"> · 自评 {{ selfDeviation.selfCode }}</template>
            </div>
          </div>

          <div class="rp-feature-box">
            <span class="rp-feature-label">典型特征</span>
            <p>{{ levelFeature }}</p>
          </div>
        </div>

        <div v-if="bucketCapped || criticalCapped" class="rp-alert">
          <span class="rp-alert-tag">木桶原理</span>
          <p>
            综合等级已由 <b>{{ rawLevel }}</b> 下修至 <b>{{ finalLevel }}</b>。
            <template v-if="weakest">
              最低能力域为「{{ weakest.name }}」（{{ weakest.level }}，得分 {{ fmt(weakest.avg) }}）。
            </template>
            <template v-if="criticalCapped">
              关键能力域「数据安全与合规 / 特定领域与场景」处于 1 档及以下，按方案 5.5 节综合等级封顶不高于 L1。
            </template>
          </p>
        </div>

        <!-- 自评预判与实评等级对照（自评不参与算分，仅作对照） -->
        <div v-if="selfDeviation" class="rp-self">
          <div class="rp-self-head">
            <span class="rp-self-tag">自评对照</span>
            <span class="rp-self-note">自评预判不参与打分，仅用于与最终评价等级对照</span>
          </div>
          <ul class="rp-self-list">
            <li>
              <span class="k">自评预判</span>
              <span class="v">
                {{ selfDeviation.selfLabel }}<em>（规范码 {{ selfDeviation.selfCode }}）</em>
              </span>
            </li>
            <li>
              <span class="k">归并等级</span>
              <span class="v">{{ selfDeviation.selfLevel }}</span>
            </li>
            <li>
              <span class="k">实评等级</span>
              <span class="v">{{ selfDeviation.finalLevel }}</span>
            </li>
            <li>
              <span class="k">偏差判断</span>
              <span
                class="v"
                :class="{
                  'is-same': selfDeviation.diff === 0,
                  'is-low': selfDeviation.diff > 0,
                  'is-high': selfDeviation.diff < 0
                }"
              >
                {{ selfDeviation.judgement }}
              </span>
            </li>
          </ul>
        </div>

        <p class="rp-note">
          评价进度：已评 {{ progress.rated }} / {{ progress.total }} 项。
          未评分项不计入得分；能力域得分为域内已评指标的算术平均值，综合得分按能力域权重加权
          <template v-if="isProject">（权重已按项目侧重点调整）</template>
          <template v-else>（采用优序图法全局权重）</template>
          。
        </p>
      </section>

      <!-- ============================ 二、能力域得分 ============================ -->
      <section class="rp-section">
        <h2 class="rp-h2"><span class="rp-h2-no">二</span>能力域得分</h2>

        <div class="rp-chart-row">
          <div class="rp-chart">
            <RadarChart ref="radarRef" :data="radarData" :height="300" />
          </div>

          <table class="rp-table rp-table-domain">
            <thead>
              <tr>
                <th style="width: 30%">能力域</th>
                <th style="width: 14%">权重</th>
                <th style="width: 16%">得分</th>
                <th style="width: 16%">所处档位</th>
                <th style="width: 24%">已评 / 总数</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="d in domainResults"
                :key="d.id"
                :class="{ 'is-weak': isWeak(d.id) }"
              >
                <td class="rp-td-name">
                  {{ d.name }}
                  <em v-if="d.critical" class="rp-tag-critical">关键</em>
                </td>
                <td>{{ pct(displayWeights[d.id]) }}</td>
                <td class="rp-td-score">{{ d.avg == null ? '未评' : fmt(d.avg) }}</td>
                <td>{{ d.level || '--' }}</td>
                <td>{{ d.rated }} / {{ d.total }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- ============================ 三、指标明细 ============================ -->
      <section class="rp-section rp-break">
        <h2 class="rp-h2"><span class="rp-h2-no">三</span>指标评分明细</h2>

        <table class="rp-table rp-table-detail">
          <thead>
            <tr>
              <th style="width: 4%">#</th>
              <th style="width: 13%">能力域</th>
              <th style="width: 15%">指标</th>
              <th style="width: 49%">评价目标</th>
              <th style="width: 12%">判定问题</th>
              <th style="width: 7%">得分</th>
              <th v-if="false" style="width: 25%">评分依据</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in detailRows" :key="row.id">
              <td class="rp-td-center">{{ i + 1 }}</td>
              <td>{{ row.domainName }}</td>
              <td class="rp-td-name">{{ row.name }}</td>
              <td class="rp-td-goal">{{ row.goal }}</td>
              <td class="rp-td-center">{{ row.answerSummary }}</td>
              <td class="rp-td-center rp-td-score">{{ row.levelText }}</td>
              <td v-if="false" class="rp-td-note">{{ row.note || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- ============================ 四、短板与建议 ============================ -->
      <section class="rp-section rp-break">
        <h2 class="rp-h2"><span class="rp-h2-no">四</span>短板分析与改进建议</h2>

        <p v-if="!weakDomains.length" class="rp-empty">
          各能力域得分均不低于综合等级所处档位，暂无明显短板。建议按 L{{ nextLevelIndex }} 的目标持续推进量化度量与智能应用。
        </p>

        <div v-for="w in weakDomains" :key="w.id" class="rp-advice">
          <div class="rp-advice-head">
            <span class="rp-advice-name">{{ w.name }}</span>
            <span class="rp-advice-level">得分 {{ fmt(w.avg) }} · {{ w.level }}</span>
          </div>
          <p class="rp-advice-text">{{ w.text }}</p>
        </div>

        <div v-if="criticalCapped" class="rp-advice rp-advice-critical">
          <div class="rp-advice-head">
            <span class="rp-advice-name">关键能力域封堵</span>
            <span class="rp-advice-level">封顶 L1</span>
          </div>
          <p class="rp-advice-text">
            {{ criticalAdvice }}
          </p>
        </div>
      </section>

      <footer class="rp-foot">
        <p>本报告由数据贯通成熟度评价系统自动生成。</p>
        <p>评价结果反映自评时点的数据贯通水平，仅供内部诊断与改进参考。生成时间：{{ stamp }}</p>
      </footer>
    </div>

    <!-- 操作区（不进入 PDF） -->
    <div class="rp-actions">
      <el-button round @click="$emit('back')">返回修改</el-button>
      <el-button type="primary" round :loading="exporting" @click="exportPDF">
        导出报告
      </el-button>
      <el-button type="danger" plain round @click="$emit('restart')">重新开始</el-button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import html2pdf from 'html2pdf.js'
import RadarChart from './RadarChart.vue'
import {
  DOMAINS, LEVELS, SCOPES, EDITIONS, ROLE_MAP, DOMAIN_ADVICE, CRITICAL_DOMAINS
} from '../data/index.js'
import { selfLevelDeviation } from '../utils/scoring.js'

const props = defineProps({
  meta: { type: Object, required: true },
  result: { type: Object, required: true },
  indicators: { type: Array, default: () => [] },
  scores: { type: Object, default: () => ({}) },
  weights: { type: Object, default: () => ({}) }
})

defineEmits(['back', 'restart'])

const radarRef = ref(null)
const exporting = ref(false)

const domainResults = computed(() => props.result.domainResults || [])
const weakDomains = computed(() => props.result.weakDomains || [])
const progress = computed(() => props.result.progress || { rated: 0, total: 0 })
const displayWeights = computed(() => props.weights || {})

const finalLevel = computed(() => props.result.finalLevel)
const rawLevel = computed(() => props.result.rawLevel)
const bucketCapped = computed(() => props.result.bucketCapped)
const criticalCapped = computed(() => props.result.criticalCapped)
const weakest = computed(() => props.result.weakest)

const isProject = computed(() => props.meta.scope === 'project')

// 自评预判 → L0-L5 归并 + 与实评等级的偏差判断
const selfDeviation = computed(() =>
  selfLevelDeviation(props.meta.selfLevel, finalLevel.value)
)

const scopeName = computed(() => (SCOPES[props.meta.scope] || {}).name || '--')
const editionName = computed(() => (EDITIONS[props.meta.edition] || {}).name || '--')
const roleName = computed(() => (ROLE_MAP[props.meta.roleId] || {}).name || '--')
const indicatorCount = computed(() => props.indicators.length)

const now = new Date()
const today = computed(() => formatDate(now))
const stamp = computed(() => `${formatDate(now)} ${pad(now.getHours())}:${pad(now.getMinutes())}`)

const scoreText = computed(() =>
  props.result.composite == null ? '--' : props.result.composite.toFixed(1)
)
const percentText = computed(() =>
  props.result.percent == null ? '--' : `${props.result.percent}%`
)

const levelMeta = computed(() => LEVELS.find((l) => l.level === finalLevel.value) || null)
const levelName = computed(() => (levelMeta.value ? levelMeta.value.name : ''))
const levelFeature = computed(() => (levelMeta.value ? levelMeta.value.feature : '—'))

const nextLevelIndex = computed(() => {
  const i = LEVELS.findIndex((l) => l.level === finalLevel.value)
  return Math.min(i + 1, LEVELS.length - 1)
})

const radarData = computed(() =>
  domainResults.value.map((d) => ({
    name: d.shortName,
    value: d.avg == null ? 0 : Number(d.avg.toFixed(2))
  }))
)

const detailRows = computed(() => {
  const domainNameOf = (id) => (DOMAINS.find((d) => d.id === id) || {}).shortName || id
  return props.indicators.map((ind) => {
    const v = props.scores[ind.id] || {}
    const answers = v.answers || {}
    let yes = 0
    let no = 0
    let unknown = 0
    Object.values(answers).forEach((a) => {
      if (a === 'yes') yes++
      else if (a === 'no') no++
      else if (a === 'unknown') unknown++
    })
    const parts = []
    if (yes) parts.push(`是 ${yes}`)
    if (no) parts.push(`否 ${no}`)
    if (unknown) parts.push(`不确定 ${unknown}`)
    return {
      id: ind.id,
      domainName: domainNameOf(ind.domain),
      name: ind.name,
      goal: ind.goal,
      answerSummary: parts.length ? parts.join(' / ') : '未勾选',
      levelText: v.level == null ? '未评' : String(v.level),
      note: v.note || ''
    }
  })
})

const criticalAdvice = computed(() =>
  CRITICAL_DOMAINS
    .map((id) => DOMAIN_ADVICE[id])
    .filter(Boolean)
    .join(' ')
)

function isWeak (id) {
  return weakDomains.value.some((w) => w.id === id)
}

function fmt (v) {
  return v == null ? '--' : Number(v).toFixed(2)
}

function pct (v) {
  return v == null ? '--' : `${(v * 100).toFixed(1)}%`
}

function pad (n) {
  return String(n).padStart(2, '0')
}

function formatDate (d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

async function exportPDF () {
  if (exporting.value) return
  exporting.value = true
  try {
    // echarts 为 canvas 渲染，必须等其 finished 后再截图，否则 PDF 中雷达图为空白
    if (radarRef.value && radarRef.value.whenRendered) {
      await radarRef.value.whenRendered()
    }
    const element = document.querySelector('#capture')
    const name = (props.meta.subjectName || '评价对象').replace(/[\\/:*?"<>|]/g, '_')
    const opt = {
      margin: [10, 10, 10, 10],
      filename: `数据贯通成熟度评价报告-${name}-${today.value}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        scrollY: 0,
        windowWidth: element.scrollWidth
      },
      pagebreak: { mode: ['css', 'avoid-all'] },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      enableLinks: true
    }
    await html2pdf().set(opt).from(element).save()
  } finally {
    exporting.value = false
  }
}
</script>

<style lang="scss" scoped>
.report-wrap {
  max-width: 940px;
  margin: 0 auto;
  padding: 24px 16px 64px;
}

/* 报告容器：宽度按 A4 比例设计，导出时作为截图根元素 */
.report {
  background: #ffffff;
  color: #1d1d1f;
  font-size: 13px;
  line-height: 1.7;
  border: 1px solid rgba(0, 0, 0, 0.10);
  border-radius: 6px;
  overflow: hidden;
}

/* ---------------- 封面 ---------------- */
.rp-cover {
  padding: 40px 46px 30px;
  border-bottom: 3px solid #0071e3;
  background: linear-gradient(180deg, rgba(0, 113, 227, 0.05), rgba(255, 255, 255, 0));
}

.rp-brand {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 12.5px;
  letter-spacing: 0.18em;
  color: #0071e3;

  .rp-brand-mark {
    width: 18px;
    height: 18px;
    border-radius: 5px;
    background: linear-gradient(135deg, #0071e3, #5ac8fa);
  }
}

.rp-title {
  margin: 22px 0 0;
  font-size: 30px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #1d1d1f;
}

.rp-subtitle {
  margin: 10px 0 0;
  font-size: 13px;
  color: #6e6e73;
}

.rp-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 28px;
  margin: 28px 0 0;
  padding: 20px 22px;
  background: rgba(120, 120, 128, 0.05);
  border-radius: 10px;
}

.rp-meta-item {
  display: flex;
  gap: 14px;
  align-items: baseline;

  dt {
    flex: 0 0 62px;
    font-size: 12.5px;
    color: #86868b;
  }

  dd {
    margin: 0;
    flex: 1;
    font-size: 13.5px;
    font-weight: 500;
    color: #1d1d1f;
    word-break: break-all;
  }
}

/* ---------------- 章节 ---------------- */
.rp-section {
  padding: 30px 46px;
  break-inside: auto;
}

.rp-break {
  break-before: page;
  page-break-before: always;
}

.rp-h2 {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 20px;
  padding-bottom: 12px;
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1f;
  border-bottom: 1px solid rgba(0, 0, 0, 0.10);
  break-after: avoid;
}

.rp-h2-no {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  font-size: 13px;
  color: #ffffff;
  background: #0071e3;
  border-radius: 7px;
}

/* ---------------- 结论 ---------------- */
.rp-conclusion {
  display: grid;
  grid-template-columns: 190px 190px minmax(0, 1fr);
  gap: 18px;
}

.rp-level-box,
.rp-score-box,
.rp-feature-box {
  padding: 18px 20px;
  border: 1px solid rgba(0, 0, 0, 0.10);
  border-radius: 12px;
  break-inside: avoid;
}

.rp-level-box {
  text-align: center;
  background: linear-gradient(160deg, rgba(0, 113, 227, 0.09), rgba(90, 200, 250, 0.05));
  border-color: rgba(0, 113, 227, 0.30);
}

.rp-level-label,
.rp-feature-label {
  display: block;
  font-size: 11.5px;
  letter-spacing: 0.14em;
  color: #86868b;
}

.rp-level-value {
  display: block;
  margin-top: 6px;
  font-size: 46px;
  font-weight: 700;
  line-height: 1.1;
  color: #0071e3;
}

.rp-level-name {
  display: block;
  margin-top: 4px;
  font-size: 14px;
  font-weight: 500;
  color: #1d1d1f;
}

.rp-score-box {
  text-align: center;
}

.rp-score-main {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  margin-top: 8px;
}

.rp-score-num {
  font-size: 36px;
  font-weight: 700;
  color: #1d1d1f;
}

.rp-score-unit {
  font-size: 13px;
  color: #86868b;
}

.rp-score-sub {
  margin-top: 6px;
  font-size: 12px;
  color: #6e6e73;
}

.rp-feature-box p {
  margin: 8px 0 0;
  font-size: 12.5px;
  line-height: 1.8;
  color: #3a3a3c;
}

.rp-alert {
  display: flex;
  gap: 12px;
  margin-top: 18px;
  padding: 14px 18px;
  background: rgba(255, 149, 0, 0.08);
  border: 1px solid rgba(255, 149, 0, 0.32);
  border-radius: 10px;
  break-inside: avoid;

  p {
    margin: 0;
    font-size: 12.5px;
    line-height: 1.8;
    color: #7a4a00;
  }

  b {
    color: #d1681f;
  }
}

.rp-alert-tag {
  flex: 0 0 auto;
  align-self: flex-start;
  padding: 2px 10px;
  font-size: 11.5px;
  color: #ffffff;
  background: #ff9500;
  border-radius: 5px;
}

/* ---------------- 自评对照 ---------------- */
.rp-self {
  margin-top: 16px;
  padding: 16px 20px;
  background: rgba(0, 113, 227, 0.04);
  border: 1px solid rgba(0, 113, 227, 0.22);
  border-radius: 10px;
  break-inside: avoid;
}

.rp-self-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 10px;
  border-bottom: 1px dashed rgba(0, 113, 227, 0.28);
}

.rp-self-tag {
  flex: 0 0 auto;
  padding: 2px 10px;
  font-size: 11.5px;
  color: #ffffff;
  background: #0071e3;
  border-radius: 5px;
}

.rp-self-note {
  font-size: 11.5px;
  color: #86868b;
}

.rp-self-list {
  margin: 10px 0 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    gap: 14px;
    padding: 6px 0;
    font-size: 12.5px;
    line-height: 1.8;
  }

  .k {
    flex: 0 0 72px;
    color: #86868b;
  }

  .v {
    flex: 1;
    color: #1d1d1f;
    font-weight: 500;

    em {
      font-style: normal;
      font-weight: 400;
      color: #86868b;
    }

    &.is-same {
      color: #1a7f37;
    }

    &.is-low {
      color: #d1681f;
    }

    &.is-high {
      color: #b3261e;
    }
  }
}

.rp-note {
  margin: 14px 0 0;
  font-size: 12px;
  line-height: 1.9;
  color: #86868b;
}

/* ---------------- 图表与表格 ---------------- */
.rp-chart-row {
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr);
  gap: 22px;
  align-items: center;
}

.rp-chart {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: #ffffff;
}

.rp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;

  th,
  td {
    padding: 9px 10px;
    border: 1px solid rgba(0, 0, 0, 0.10);
    text-align: left;
    vertical-align: middle;
  }

  thead th {
    background: rgba(0, 113, 227, 0.07);
    font-weight: 600;
    color: #1d1d1f;
    break-inside: avoid;
  }

  tbody tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  tbody tr.is-weak {
    background: rgba(255, 59, 48, 0.05);
  }
}

.rp-td-center {
  text-align: center;
}

.rp-td-name {
  font-weight: 500;
}

.rp-td-score {
  font-weight: 600;
  color: #0071e3;
}

.rp-td-goal,
.rp-td-note {
  font-size: 11.5px;
  line-height: 1.7;
  color: #4a4a52;
}

.rp-tag-critical {
  margin-left: 6px;
  padding: 1px 6px;
  font-style: normal;
  font-size: 10.5px;
  color: #d1681f;
  background: rgba(209, 104, 31, 0.10);
  border-radius: 3px;
}

/* ---------------- 短板与建议 ---------------- */
.rp-empty {
  margin: 0;
  padding: 16px 18px;
  font-size: 12.5px;
  color: #3a3a3c;
  background: rgba(52, 199, 89, 0.06);
  border: 1px solid rgba(52, 199, 89, 0.25);
  border-radius: 10px;
}

.rp-advice {
  margin-top: 14px;
  padding: 16px 20px;
  border: 1px solid rgba(0, 0, 0, 0.10);
  border-left: 3px solid #0071e3;
  border-radius: 8px;
  break-inside: avoid;

  &.rp-advice-critical {
    border-left-color: #d1681f;
    background: rgba(209, 104, 31, 0.04);
  }
}

.rp-advice-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 14px;
}

.rp-advice-name {
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
}

.rp-advice-level {
  font-size: 12px;
  color: #86868b;
  white-space: nowrap;
}

.rp-advice-text {
  margin: 8px 0 0;
  font-size: 12.5px;
  line-height: 1.9;
  color: #3a3a3c;
}

/* ---------------- 页脚 ---------------- */
.rp-foot {
  padding: 20px 46px 26px;
  border-top: 1px solid rgba(0, 0, 0, 0.10);
  background: rgba(120, 120, 128, 0.04);

  p {
    margin: 0;
    font-size: 11.5px;
    line-height: 1.8;
    color: #86868b;
    text-align: center;
  }
}

/* ---------------- 操作区 ---------------- */
.rp-actions {
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-top: 22px;
}

@media (max-width: 860px) {
  .rp-cover,
  .rp-section,
  .rp-foot {
    padding-left: 22px;
    padding-right: 22px;
  }

  .rp-conclusion {
    grid-template-columns: minmax(0, 1fr);
  }

  .rp-chart-row {
    grid-template-columns: minmax(0, 1fr);
  }

  .rp-meta {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
